/**
 * The Astalabista mark.
 *
 * The old logo was a rounded square containing a glyph that read as a
 * capital G — it belonged to no particular company and said nothing about
 * this one.
 *
 * This mark is an A whose crossbar does not stop at the letterform. It
 * runs past the right stroke and terminates in a detached node, which is
 * the site's entire argument compressed into one glyph: the line keeps
 * going past the point where most suppliers hand over, and ends at
 * something that is running. The two diagonals carry the emerald → violet
 * gradient; the node is solid emerald, the same colour used everywhere on
 * the site to mean "in production".
 *
 * Geometry notes, because they are easy to break:
 *   - The crossbar's left end (18.4) is computed to land exactly on the
 *     left diagonal at y=36. Move a diagonal and it has to move too.
 *   - The gap between the crossbar end (43) and the node (51, r=4) is
 *     deliberate. At favicon size it closes up visually, which is fine —
 *     the A still reads. At any larger size the detachment is the point.
 *   - Everything is stroked, not filled, so a single stroke-width change
 *     rescales the whole mark's weight.
 */

const GRADIENT_STOPS = (
  <>
    <stop offset="0" stopColor="#10b981" />
    <stop offset="0.5" stopColor="#14b8a6" />
    <stop offset="1" stopColor="#8b5cf6" />
  </>
);

export function Logo({
  className = "h-8 w-8",
  /** Wrap the glyph in the dark rounded square. Used where the mark needs
   *  its own ground — a browser tab, an OG image — rather than sitting on
   *  the site's own dark background. */
  boxed = false,
  /** Unique gradient id. Two inline SVGs sharing one id in the same
   *  document is a real bug: the second element resolves the first's
   *  gradient, and in some browsers the first one wins for both. */
  id = "asta-mark",
}: {
  className?: string;
  boxed?: boolean;
  id?: string;
}) {
  const glyph = (
    <g
      fill="none"
      stroke={`url(#${id})`}
      strokeWidth="5.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 50 L28 15 L44 50" />
      <path d="M18.4 36 H43" />
    </g>
  );

  return (
    <svg
      viewBox="0 0 64 64"
      className={className}
      role="img"
      aria-label="astalabista"
    >
      <defs>
        <linearGradient
          id={id}
          x1="10"
          y1="12"
          x2="54"
          y2="52"
          gradientUnits="userSpaceOnUse"
        >
          {GRADIENT_STOPS}
        </linearGradient>
      </defs>

      {boxed ? (
        <>
          <rect width="64" height="64" rx="14" fill="#0e1024" />
          <g transform="translate(32 32) scale(0.8) translate(-32 -32)">
            {glyph}
            <circle cx="51" cy="36" r="4" fill="#10b981" />
          </g>
        </>
      ) : (
        <>
          {glyph}
          <circle cx="51" cy="36" r="4" fill="#10b981" />
        </>
      )}
    </svg>
  );
}

/**
 * The wordmark, as a component so the nav and footer cannot drift apart.
 * Kept lowercase and recognisable — the brief was explicit that
 * "astalabista" should still read as itself.
 */
export function Wordmark({ className = "" }: { className?: string }) {
  return (
    <span
      className={`font-display font-semibold tracking-[-0.03em] ${className}`}
    >
      astalabista
    </span>
  );
}
